import { useEffect, useMemo, useState } from 'react';
import { Modal } from 'antd';
import DataTable from '../../../../reusable/DataTable/DataTable';
import Skeleton from '../../../../reusable/Skeleton/Skeleton';
import {
  useGetAffiliateCodeCountriesQuery,
  useGetAffiliateCodeReferralsQuery,
} from '../../../../store/api/affiliateApi';
import { usd, NA } from '../../../../utilities/format';
import { countryLabel, formatDate } from '../format';
import CountryDonut from './CountryDonut';
import { t } from '../../../../i18n/i18n';
import './CodeDetailsDialog.scss';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

const PAGE_SIZE = 10;

/** Live splits a full `name` when the row has one, else reads the two fields. */
const firstName = (r) => (r.name ? r.name.split(' ')[0] : r.first_name) || NA;
const lastName = (r) => (r.name ? r.name.split(' ').slice(1).join(' ') : r.last_name) || NA;

const columns = [
  { key: 'first_name', header: 'First Name', width: 12, value: firstName },
  { key: 'last_name', header: 'Last Name', width: 12, value: lastName },
  { key: 'Country', header: 'Country', width: 14, value: (r) => countryLabel(r.Country) },
  { key: 'date_joined', header: 'Date', width: 12, value: (r) => formatDate(r.date_joined) },
  { key: 'account_size', header: 'Account Size', width: 11, value: (r) => r.account_size || NA },
  { key: 'account_price', header: 'Account Price', width: 11, value: (r) => usd(r.account_price ?? 0) },
  {
    key: 'commission',
    header: 'Commission Earned',
    width: 13,
    value: (r) => usd(r.commission ?? 0),
    tone: () => 'primary',
  },
];

/**
 * Code Details — what live's "View Details" opens (`ModalCard` in
 * `AffiliateTable.jsx` on main): the code's totals, the countries its sign-ups
 * came from, and every referred trader, paged on the server.
 */
export default function CodeDetailsDialog({ code, onClose }) {
  const codeId = code?.coupon_id;
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);

  // A different code starts on its own first page.
  useEffect(() => {
    setPage(1);
    setSelected(null);
  }, [codeId]);

  const summary = useGetAffiliateCodeCountriesQuery({ affiliate_code_id: codeId }, { skip: codeId == null });
  const referrals = useGetAffiliateCodeReferralsQuery(
    { affiliate_code_id: codeId, page, page_size: PAGE_SIZE },
    { skip: codeId == null }
  );

  const stats = summary.currentData;
  const countries = useMemo(
    () =>
      (stats?.countries ?? [])
        .map((c) => ({
          name: c.Country,
          label: countryLabel(c.Country),
          joined: Number(c.new_joinee) || 0,
          revenue: c.revenue,
        }))
        .sort((a, b) => b.joined - a.joined),
    [stats]
  );

  // Opens on the country with the most sign-ups, as live does.
  const shown = countries.find((c) => c.name === selected) ?? countries[0] ?? null;

  const rows = referrals.currentData?.results ?? [];
  const total = referrals.currentData?.count ?? rows.length;
  const summaryLoading = summary.isFetching && !stats;

  const STATS = [
    { key: 'code', label: t('Code'), icon: 'affiliate-signups', value: stats?.code_name || code?.code || NA },
    { key: 'revenue', label: t('Total Revenue'), icon: 'affiliate-revenue', value: usd(stats?.total_revenue ?? 0) },
    { key: 'joined', label: t('Total Joined'), icon: 'affiliate-new-signup-revenue', value: stats?.total_joinee ?? 0 },
  ];

  return (
    <Modal
      open={codeId != null}
      onCancel={onClose}
      footer={null}
      centered
      width={980}
      destroyOnHidden
      className="aff-dialog"
      rootClassName="aff-dialog-root"
      aria-label={t('Code details')}
    >
      <div className="code-details">
        <h2 className="aff-dialog__title">{t('Code Details')}</h2>

        <div className="code-details__stats">
          {STATS.map((s) => (
            <div className="code-details__stat" key={s.key}>
              <span className="code-details__stat-label">
                <span className="icon code-details__stat-icon" style={icon(s.icon)} />
                {s.label}
              </span>
              <span className="code-details__stat-value" title={String(s.value)}>
                {summaryLoading ? <Skeleton w="90px" h={20} /> : s.value}
              </span>
            </div>
          ))}
        </div>

        <div className="code-details__body">
          <section className="code-details__countries">
            <h3 className="code-details__heading">{t('Countries — code used in')}</h3>

            {summaryLoading ? (
              <Skeleton w="100%" h={220} />
            ) : countries.length ? (
              <>
                <div className="code-details__country">
                  <span className="code-details__country-name">{shown?.label ?? NA}</span>
                  <dl className="code-details__country-stats">
                    <div>
                      <dt>{t('Total joined')}</dt>
                      <dd>{shown?.joined ?? 0}</dd>
                    </div>
                    <div>
                      <dt>{t('Total revenue')}</dt>
                      <dd>{usd(shown?.revenue ?? 0)}</dd>
                    </div>
                  </dl>
                </div>
                <CountryDonut countries={countries} selected={shown?.name} onSelect={setSelected} />
              </>
            ) : (
              <p className="code-details__empty">
                {summary.isError ? t('Could not load the countries') : t('No sign-ups on this code yet')}
              </p>
            )}
          </section>

          <DataTable
            className="code-details__table"
            icon="affiliate-recent-requests"
            title="Details"
            columns={columns}
            rows={rows}
            getRowId={(r, i) => r.id ?? i}
            minWidth={720}
            total={total}
            page={page}
            onPageChange={setPage}
            pageSize={PAGE_SIZE}
            loading={referrals.isFetching}
            emptyMessage={referrals.isError ? t('Could not load the referrals') : t('No referral data available')}
          />
        </div>
      </div>
    </Modal>
  );
}
